'use client';

import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { removeCookies } from '@/helpers/cookies';
import { getFromLocalStorage, setToLocalStorage } from '@/helpers/localStorage';
import { isGAEnabled, updateConsent } from '@/lib/analytics';

import { SHOW_COOKIE_BANNER, USE_OPTIONAL_COOKIES } from './CookieBanner';

interface CookieSettingsType {
  enableOptionalCookies: boolean;
  enableAnalytics?: boolean;
}

interface CookieContextType {
  showBanner: boolean;
  setShowBanner: Dispatch<SetStateAction<boolean>>;
  optionalCookies: boolean;
  setOptionalCookies: Dispatch<SetStateAction<boolean>>;
  analyticsConsent: boolean;
  setAnalyticsConsent: Dispatch<SetStateAction<boolean>>;
  setCookieSettings: (setting: CookieSettingsType) => void;
  setBannerVisible: (value: boolean) => void;
  showCookieSettings: boolean;
  setShowCookieSettings: Dispatch<SetStateAction<boolean>>;
}

export const CookieContext = createContext<CookieContextType>({
  showBanner: false,
  setShowBanner: () => {},
  optionalCookies: false,
  setOptionalCookies: () => {},
  analyticsConsent: false,
  setAnalyticsConsent: () => {},
  setCookieSettings: () => {},
  setBannerVisible: () => {},
  showCookieSettings: false,
  setShowCookieSettings: () => {},
});

const ANALYTICS_CONSENT_KEY = 'analyticsConsent';

// Helper functions for useSyncExternalStore
const subscribe = () => () => {}; // localStorage doesn't need subscription
const getShowBannerSnapshot = () =>
  getFromLocalStorage(SHOW_COOKIE_BANNER) !== 'false';
const getOptionalCookiesSnapshot = () =>
  getFromLocalStorage(USE_OPTIONAL_COOKIES) === 'true';
const getAnalyticsConsentSnapshot = () =>
  getFromLocalStorage(ANALYTICS_CONSENT_KEY) === 'true';
const getServerSnapshot = () => false; // Default to false during SSR

function CookieProvider({ children }: PropsWithChildren) {
  // Use useSyncExternalStore to avoid hydration mismatch
  const showBannerFromStorage = useSyncExternalStore(
    subscribe,
    getShowBannerSnapshot,
    getServerSnapshot
  );
  const optionalCookiesFromStorage = useSyncExternalStore(
    subscribe,
    getOptionalCookiesSnapshot,
    getServerSnapshot
  );
  const analyticsConsentFromStorage = useSyncExternalStore(
    subscribe,
    getAnalyticsConsentSnapshot,
    getServerSnapshot
  );

  // Use internal state for updates
  const [showBanner, setShowBanner] = useState(showBannerFromStorage);
  const [optionalCookies, setOptionalCookies] = useState(
    optionalCookiesFromStorage
  );
  const [analyticsConsent, setAnalyticsConsent] = useState(
    analyticsConsentFromStorage
  );
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  const [syncedWithStorage, setSyncedWithStorage] = useState({
    showBanner: showBannerFromStorage,
    optionalCookies: optionalCookiesFromStorage,
    analyticsConsent: analyticsConsentFromStorage,
  });

  // Take up the stored choices once the client snapshot replaces the server's
  // defaults. Adjusted during render rather than in an effect: React redoes the
  // render before painting, so someone who already dismissed the banner never
  // sees it flash back.
  if (
    syncedWithStorage.showBanner !== showBannerFromStorage ||
    syncedWithStorage.optionalCookies !== optionalCookiesFromStorage ||
    syncedWithStorage.analyticsConsent !== analyticsConsentFromStorage
  ) {
    setSyncedWithStorage({
      showBanner: showBannerFromStorage,
      optionalCookies: optionalCookiesFromStorage,
      analyticsConsent: analyticsConsentFromStorage,
    });
    setShowBanner(showBannerFromStorage);
    setOptionalCookies(optionalCookiesFromStorage);
    setAnalyticsConsent(analyticsConsentFromStorage);
  }

  // Update Google Analytics consent when analyticsConsent changes
  useEffect(() => {
    if (isGAEnabled()) {
      updateConsent(analyticsConsent);
    }
  }, [analyticsConsent]);

  const setCookieSettings = useCallback(
    ({ enableOptionalCookies, enableAnalytics }: CookieSettingsType) => {
      setToLocalStorage(USE_OPTIONAL_COOKIES, String(enableOptionalCookies));

      // Handle analytics consent
      const newAnalyticsConsent = enableAnalytics ?? enableOptionalCookies;
      setToLocalStorage(ANALYTICS_CONSENT_KEY, String(newAnalyticsConsent));
      setAnalyticsConsent(newAnalyticsConsent);

      if (!enableOptionalCookies) {
        // If the user has opted out of optional cookies, remove all cookies
        removeCookies();
      }
      setOptionalCookies(enableOptionalCookies);
    },
    [setOptionalCookies, setAnalyticsConsent]
  );

  const setBannerVisible = useCallback(
    (value: boolean) => {
      setToLocalStorage(SHOW_COOKIE_BANNER, String(value));
      setShowBanner(value);
    },
    [setShowBanner]
  );

  const contextValue = useMemo(
    () => ({
      showBanner,
      setShowBanner,
      optionalCookies,
      setOptionalCookies,
      analyticsConsent,
      setAnalyticsConsent,
      setCookieSettings,
      setBannerVisible,
      showCookieSettings,
      setShowCookieSettings,
    }),
    [
      showBanner,
      setShowBanner,
      optionalCookies,
      analyticsConsent,
      setAnalyticsConsent,
      setCookieSettings,
      setBannerVisible,
      showCookieSettings,
      setShowCookieSettings,
    ]
  );

  return (
    <CookieContext.Provider value={contextValue}>
      {children}
    </CookieContext.Provider>
  );
}

export default CookieProvider;
export const useCookieContext = () => useContext(CookieContext);
